import React, { useEffect } from 'react';

import { connect } from 'react-redux';

import { getQueryKey } from 'common/actions/ideas';
import { useIdeasContext } from 'common/subdomain/admin/v2/containers/IdeasContainer';
import { Span } from 'common/ui/Text';
import styles from 'css-module/components/subdomain/admin/v2/AdminV2IdeaDrawer/_IdeaSearch.module.scss';

import type { Group } from 'common/api/endpoints/group';
import type { Idea } from 'common/api/endpoints/idea';
import type { GroupsState } from 'common/reducers/groups';
import type { IdeasState } from 'common/reducers/ideas';

type ConnectProps = {
  groupAncestors: (Group | null)[];
  ideaAncestors: (Idea | null)[];
};
type OwnProps = {
  idea: Idea;
};

type Props = OwnProps & ConnectProps;

function IdeaSearchAncestors({ groupAncestors, idea, ideaAncestors }: Props) {
  const { loadIdea } = useIdeasContext();
  useEffect(() => {
    idea.ancestorIDs.forEach((ancestorID) => {
      loadIdea({ ideaID: ancestorID });
    });
  }, [idea, loadIdea]);

  const ancestors = [...groupAncestors, ...ideaAncestors];
  const path = ancestors
    .map((ancestor: Group | Idea | null) => {
      if (!ancestor) {
        return '...';
      }
      const isIdea = 'title' in ancestor;
      if (isIdea) {
        return ancestor.title;
      } else {
        return ancestor.name;
      }
    })
    .join(' > ');

  if (path.length === 0) {
    return null;
  }

  return (
    <Span className={styles.ideaPath} variant="bodySm">
      {path}
    </Span>
  );
}

const isGroupAncestorID = (idea: Idea, ancestorID: string) => {
  const lastGroupAncestorIndex = idea.groupID ? idea.ancestorIDs.indexOf(idea.groupID) : -1;
  const ancestorIndex = idea.ancestorIDs.indexOf(ancestorID);
  return ancestorIndex <= lastGroupAncestorIndex;
};

export default connect(
  ({ ideas, groups }: { ideas: IdeasState; groups: GroupsState }, { idea }: OwnProps) => {
    const groupAncestorIDs = idea.ancestorIDs.filter((ancestorID) => {
      return isGroupAncestorID(idea, ancestorID);
    });
    const groupAncestors = groupAncestorIDs.map((ancestorID) => {
      return groups.data!.find((group) => group._id === ancestorID)!;
    });

    const ideaAncestorIDs = idea.ancestorIDs.filter((ancestorID) => {
      return !isGroupAncestorID(idea, ancestorID);
    });
    const ideaAncestors = ideaAncestorIDs.map((ancestorID) => {
      const queryKey = getQueryKey({ ideaID: ancestorID });
      return ideas[queryKey]?.items?.[0] ?? null;
    });

    return { groupAncestors, ideaAncestors };
  },
)(IdeaSearchAncestors) as unknown as React.FC<OwnProps>;
